import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ArrowLeft, Plus, Camera, Info } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from './ui/Button';
import { Card } from './ui/Card';
import { ReportConfirmationModal } from './ReportConfirmationModal';
type ProblemType = 'excessiveDirt' | 'missingMaterial' | 'brokenItem' | 'other';
interface ReportProblemModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: any) => void;
}
interface Step2Data {
  canResolve: 'yesWithTime' | 'noBlocking' | null;
  extraTime: string | null;
  affectedArea: string | null;
  damageSeverity: string | null;
  description: string;
}
const problemOptions: Array<{
  id: ProblemType;
  emoji: string;
  titleKey: string;
  descriptionKey: string;
}> = [{
  id: 'excessiveDirt',
  emoji: '🧹',
  titleKey: 'excessiveDirt',
  descriptionKey: 'excessiveDirt'
}, {
  id: 'missingMaterial',
  emoji: '📦',
  titleKey: 'missingMaterial',
  descriptionKey: 'missingMaterial'
}, {
  id: 'brokenItem',
  emoji: '🔧',
  titleKey: 'brokenItem',
  descriptionKey: 'brokenItem'
}, {
  id: 'other',
  emoji: '⚠️',
  titleKey: 'other',
  descriptionKey: 'other'
}];
// Mapping severity -> label e colore pallino
const severityLabels: Record<number, {
  label: string;
  color: string;
  dotColor: string;
}> = {
  1: {
    label: 'Minimo',
    color: 'text-green-600',
    dotColor: 'bg-green-500'
  },
  2: {
    label: 'Lieve',
    color: 'text-yellow-600',
    dotColor: 'bg-yellow-500'
  },
  3: {
    label: 'Moderato',
    color: 'text-orange-500',
    dotColor: 'bg-orange-500'
  },
  4: {
    label: 'Grave',
    color: 'text-red-500',
    dotColor: 'bg-red-500'
  },
  5: {
    label: 'Critico',
    color: 'text-red-700',
    dotColor: 'bg-red-700'
  }
};
export function ReportProblemModal({
  isOpen,
  onClose,
  onSubmit
}: ReportProblemModalProps) {
  const {
    t
  } = useLanguage();
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedProblem, setSelectedProblem] = useState<ProblemType | null>(null);
  const [step2Data, setStep2Data] = useState<Step2Data>({
    canResolve: null,
    extraTime: null,
    affectedArea: null,
    damageSeverity: null,
    description: ''
  });
  const [photos, setPhotos] = useState<string[]>([]);
  const [showConfirmation, setShowConfirmation] = useState(false);
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
      setCurrentStep(1);
      setSelectedProblem(null);
      setStep2Data({
        canResolve: null,
        extraTime: null,
        affectedArea: null,
        damageSeverity: null,
        description: ''
      });
      setPhotos([]);
      setShowConfirmation(false);
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);
  const handleStep1Continue = () => {
    if (selectedProblem) {
      setCurrentStep(2);
    }
  };
  const handleStep2Continue = () => {
    if (isStep2Valid()) {
      setCurrentStep(3);
    }
  };
  const handleStep3Continue = () => {
    setCurrentStep(4);
  };
  const handleFinalSubmit = () => {
    onSubmit({
      problemType: selectedProblem,
      ...step2Data,
      photos
    });
    setShowConfirmation(true);
  };
  const handleConfirmationClose = () => {
    setShowConfirmation(false);
    onClose();
  };
  const handleAddPhoto = () => {
    const mockPhotoUrl = `https://images.unsplash.com/photo-1584622650111-993a426fbf0a?auto=format&fit=crop&w=300&q=80&sig=${Date.now()}`;
    setPhotos([...photos, mockPhotoUrl]);
  };
  const handleRemovePhoto = (index: number) => {
    setPhotos(photos.filter((_, i) => i !== index));
  };
  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };
  const handleSeverityChange = (rating: number) => {
    setStep2Data({
      ...step2Data,
      damageSeverity: rating.toString()
    });
  };
  const isStep2Valid = () => {
    if (!step2Data.canResolve || !step2Data.affectedArea || !step2Data.description.trim()) {
      return false;
    }
    if (step2Data.canResolve === 'yesWithTime' && !step2Data.extraTime) {
      return false;
    }
    if (selectedProblem === 'brokenItem' && !step2Data.damageSeverity) {
      return false;
    }
    return true;
  };
  const isStep3Valid = () => {
    if (selectedProblem === 'missingMaterial') {
      return true;
    }
    return photos.length > 0;
  };
  const getStep3Subtitle = () => {
    if (selectedProblem === 'missingMaterial') {
      return t.fornitore.reportProblem.step3.subtitleOptional;
    }
    return t.fornitore.reportProblem.step3.subtitleRequired;
  };
  const getProblemTypeLabel = () => {
    if (!selectedProblem) return '';
    const option = problemOptions.find(o => o.id === selectedProblem);
    if (!option) return '';
    return t.fornitore.reportProblem.step1.options[option.titleKey].title;
  };
  const getProblemTypeEmoji = () => {
    if (!selectedProblem) return '';
    const option = problemOptions.find(o => o.id === selectedProblem);
    return option?.emoji || '';
  };
  const getResolvabilityBadge = () => {
    if (step2Data.canResolve === 'noBlocking') {
      return {
        text: t.fornitore.reportProblem.step4.badges.blocking,
        color: 'bg-red-100 text-red-700 border-red-200'
      };
    }
    if (step2Data.canResolve === 'yesWithTime' && step2Data.extraTime) {
      const timeLabel = t.fornitore.reportProblem.step2.extraTime.options[step2Data.extraTime];
      return {
        text: t.fornitore.reportProblem.step4.badges.resolvableWith.replace('{time}', timeLabel),
        color: 'bg-yellow-100 text-yellow-700 border-yellow-200'
      };
    }
    return null;
  };
  const getEstimatedTime = () => {
    if (step2Data.extraTime) {
      return t.fornitore.reportProblem.step2.extraTime.options[step2Data.extraTime];
    }
    return undefined;
  };
  const getSeverityLabel = () => {
    if (!step2Data.damageSeverity) return '';
    const rating = parseInt(step2Data.damageSeverity);
    return severityLabels[rating]?.label || '';
  };
  return <AnimatePresence data-id="element-335">
      {isOpen && !showConfirmation && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-black/50 z-50" data-id="element-336" />

          {/* Modal */}
          <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-337">
            <div className="flex min-h-full items-center justify-center p-4" data-id="element-338">
              <motion.div initial={{
            opacity: 0,
            scale: 0.95,
            y: 20
          }} animate={{
            opacity: 1,
            scale: 1,
            y: 0
          }} exit={{
            opacity: 0,
            scale: 0.95,
            y: 20
          }} transition={{
            type: 'spring',
            damping: 25,
            stiffness: 300
          }} onClick={e => e.stopPropagation()} className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl flex flex-col max-h-[90vh]" data-id="element-339">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0" data-id="element-340">
                  <div className="flex items-center gap-3 flex-1" data-id="element-341">
                    {currentStep > 1 && <button onClick={handleBack} className="p-2 hover:bg-gray-100 rounded-full transition-colors" data-id="element-342">
                        <ArrowLeft className="w-5 h-5 text-gray-600" data-id="element-343" />
                      </button>}
                    <div className="flex-1" data-id="element-344">
                      <h2 className="text-2xl font-bold text-navy" data-id="element-345">
                        {t.fornitore.reportProblem.title}
                      </h2>
                      <p className="text-sm text-gray-500 mt-1" data-id="element-346">
                        {t.fornitore.reportProblem.step.replace('{current}', currentStep.toString()).replace('{total}', '4')}
                      </p>
                    </div>
                  </div>
                  <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors" data-id="element-347">
                    <X className="w-6 h-6 text-gray-600" data-id="element-348" />
                  </button>
                </div>

                {/* Scrollable Content */}
                <div className="flex-1 overflow-y-auto p-6" data-id="element-349">
                  {currentStep === 1 && <div className="space-y-6" data-id="element-350">
                      <h3 className="text-xl font-bold text-navy" data-id="element-351">
                        {t.fornitore.reportProblem.step1.title}
                      </h3>

                      <div className="space-y-4" data-id="element-352">
                        {problemOptions.map((option, index) => <motion.div key={option.id} initial={{
                    opacity: 0,
                    y: 20
                  }} animate={{
                    opacity: 1,
                    y: 0
                  }} transition={{
                    delay: index * 0.1
                  }} data-id="element-353">
                            <Card onClick={() => setSelectedProblem(option.id)} className={`p-6 cursor-pointer transition-all duration-200 ${selectedProblem === option.id ? 'border-2 border-teal-deep bg-teal-50/30 shadow-md' : 'border-2 border-gray-200 hover:border-gray-300 hover:shadow-sm'}`} data-id="element-354">
                              <div className="flex items-start gap-4" data-id="element-355">
                                <div className="text-5xl flex-shrink-0" data-id="element-356">
                                  {option.emoji}
                                </div>
                                <div className="flex-1 min-w-0" data-id="element-357">
                                  <h4 className="text-lg font-bold text-navy mb-1" data-id="element-358">
                                    {t.fornitore.reportProblem.step1.options[option.titleKey].title}
                                  </h4>
                                  <p className="text-gray-600 text-sm" data-id="element-359">
                                    {t.fornitore.reportProblem.step1.options[option.descriptionKey].description}
                                  </p>
                                </div>
                                {selectedProblem === option.id && <motion.div initial={{
                          scale: 0
                        }} animate={{
                          scale: 1
                        }} className="flex-shrink-0 w-6 h-6 rounded-full bg-teal-deep flex items-center justify-center" data-id="element-360">
                                    <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-361">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" data-id="element-362" />
                                    </svg>
                                  </motion.div>}
                              </div>
                            </Card>
                          </motion.div>)}
                      </div>
                    </div>}

                  {currentStep === 2 && <motion.div initial={{
                opacity: 0,
                x: 20
              }} animate={{
                opacity: 1,
                x: 0
              }} className="space-y-6" data-id="element-363">
                      <h3 className="text-xl font-bold text-navy" data-id="element-364">
                        {t.fornitore.reportProblem.step2.title}
                      </h3>

                      <div data-id="element-365">
                        <label className="block text-sm font-semibold text-gray-700 mb-3" data-id="element-366">
                          {t.fornitore.reportProblem.step2.canResolve.question}
                        </label>
                        <div className="space-y-2" data-id="element-367">
                          {['yesWithTime', 'noBlocking'].map(option => <Card key={option} onClick={() => setStep2Data({
                      ...step2Data,
                      canResolve: option as 'yesWithTime' | 'noBlocking',
                      extraTime: option === 'noBlocking' ? null : step2Data.extraTime
                    })} className={`p-4 cursor-pointer transition-all ${step2Data.canResolve === option ? 'border-2 border-teal-deep bg-teal-50/30' : 'border-2 border-gray-200 hover:border-gray-300'}`} data-id="element-368">
                              <div className="flex items-center justify-between" data-id="element-369">
                                <span className="font-medium text-navy" data-id="element-370">
                                  {t.fornitore.reportProblem.step2.canResolve[option]}
                                </span>
                                {step2Data.canResolve === option && <div className="w-5 h-5 rounded-full bg-teal-deep flex items-center justify-center" data-id="element-371">
                                    <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-372">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" data-id="element-373" />
                                    </svg>
                                  </div>}
                              </div>
                            </Card>)}
                        </div>
                      </div>

                      {step2Data.canResolve === 'yesWithTime' && <motion.div initial={{
                  opacity: 0,
                  height: 0
                }} animate={{
                  opacity: 1,
                  height: 'auto'
                }} exit={{
                  opacity: 0,
                  height: 0
                }} data-id="element-374">
                          <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-375">
                            {t.fornitore.reportProblem.step2.extraTime.label}
                          </label>
                          <select value={step2Data.extraTime || ''} onChange={e => setStep2Data({
                    ...step2Data,
                    extraTime: e.target.value
                  })} className="w-full h-12 px-4 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-navy focus:border-transparent outline-none transition-all" data-id="element-376">
                            <option value="" data-id="element-377">Seleziona...</option>
                            {Object.entries(t.fornitore.reportProblem.step2.extraTime.options).map(([key, label]) => <option key={key} value={key} data-id="element-378">
                                {label}
                              </option>)}
                          </select>
                        </motion.div>}

                      <div data-id="element-379">
                        <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-380">
                          {t.fornitore.reportProblem.step2.affectedArea.label}
                        </label>
                        <select value={step2Data.affectedArea || ''} onChange={e => setStep2Data({
                    ...step2Data,
                    affectedArea: e.target.value
                  })} className="w-full h-12 px-4 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-navy focus:border-transparent outline-none transition-all" data-id="element-381">
                          <option value="" data-id="element-382">Seleziona...</option>
                          {Object.entries(t.fornitore.reportProblem.step2.affectedArea.options).map(([key, label]) => <option key={key} value={key} data-id="element-383">
                              {label}
                            </option>)}
                        </select>
                      </div>

                      {selectedProblem === 'brokenItem' && <motion.div initial={{
                  opacity: 0,
                  height: 0
                }} animate={{
                  opacity: 1,
                  height: 'auto'
                }} data-id="element-384">
                          <label className="block text-sm font-semibold text-gray-700 mb-3" data-id="element-385">
                            {t.fornitore.reportProblem.step2.damageSeverity.label}
                          </label>

                          {/* Dot Rating */}
                          <div className="flex items-center gap-2 mb-3" data-id="element-386">
                            {[1, 2, 3, 4, 5].map(level => <motion.button key={level} type="button" onClick={() => handleSeverityChange(level)} whileTap={{
                      scale: 0.9
                    }} className="p-1 rounded-full hover:bg-gray-50 transition-colors touch-manipulation" data-id="element-387">
                                <div className={`w-8 h-8 rounded-full border-2 transition-all ${step2Data.damageSeverity && level <= parseInt(step2Data.damageSeverity) ? `${severityLabels[parseInt(step2Data.damageSeverity)]?.dotColor} border-transparent` : 'bg-gray-100 border-gray-300'}`} data-id="element-388" />
                              </motion.button>)}
                          </div>

                          {/* Severity Label */}
                          {step2Data.damageSeverity && <motion.div initial={{
                    opacity: 0,
                    y: -10
                  }} animate={{
                    opacity: 1,
                    y: 0
                  }} className="flex items-center gap-2" data-id="element-389">
                              <span className="text-sm text-gray-500" data-id="element-390">
                                Livello di gravità:
                              </span>
                              <span className={`text-base font-semibold ${severityLabels[parseInt(step2Data.damageSeverity)]?.color}`} data-id="element-391">
                                {getSeverityLabel()}
                              </span>
                            </motion.div>}
                        </motion.div>}

                      <div data-id="element-392">
                        <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-393">
                          {t.fornitore.reportProblem.step2.description.label}
                        </label>
                        <textarea value={step2Data.description} onChange={e => setStep2Data({
                    ...step2Data,
                    description: e.target.value
                  })} placeholder={t.fornitore.reportProblem.step2.description.placeholder} rows={4} className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-navy focus:border-transparent outline-none transition-all resize-none" data-id="element-394" />
                      </div>
                    </motion.div>}

                  {currentStep === 3 && <motion.div initial={{
                opacity: 0,
                x: 20
              }} animate={{
                opacity: 1,
                x: 0
              }} className="space-y-6" data-id="element-395">
                      <div data-id="element-396">
                        <h3 className="text-xl font-bold text-navy mb-2" data-id="element-397">
                          {t.fornitore.reportProblem.step3.title}
                        </h3>
                        <p className="text-sm text-gray-500" data-id="element-398">
                          {getStep3Subtitle()}
                        </p>
                      </div>

                      <div className="grid grid-cols-2 gap-4" data-id="element-399">
                        {photos.length === 0 ? <motion.button onClick={handleAddPhoto} whileHover={{
                    scale: 1.02
                  }} whileTap={{
                    scale: 0.98
                  }} className="col-span-2 aspect-[16/9] border-2 border-dashed border-gray-300 rounded-xl hover:border-teal-deep hover:bg-teal-50/30 transition-all flex flex-col items-center justify-center gap-3 group" data-id="element-400">
                            <div className="w-16 h-16 rounded-full bg-gray-100 group-hover:bg-teal-100 flex items-center justify-center transition-colors" data-id="element-401">
                              <Plus className="w-8 h-8 text-gray-400 group-hover:text-teal-deep transition-colors" data-id="element-402" />
                            </div>
                            <span className="text-gray-600 group-hover:text-teal-deep font-medium transition-colors" data-id="element-403">
                              {t.fornitore.reportProblem.step3.addPhoto}
                            </span>
                          </motion.button> : <>
                            {photos.map((photo, index) => <motion.div key={index} initial={{
                      opacity: 0,
                      scale: 0.8
                    }} animate={{
                      opacity: 1,
                      scale: 1
                    }} exit={{
                      opacity: 0,
                      scale: 0.8
                    }} className={`relative aspect-[4/3] rounded-xl overflow-hidden border-2 border-gray-200 group ${index === 0 ? 'col-span-2' : ''}`} data-id="element-404">
                                <img src={photo} alt={`Photo ${index + 1}`} className="w-full h-full object-cover" data-id="element-405" />
                                <button onClick={() => handleRemovePhoto(index)} className="absolute top-2 right-2 w-8 h-8 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-lg" data-id="element-406">
                                  <X className="w-5 h-5" data-id="element-407" />
                                </button>
                              </motion.div>)}

                            {photos.length < 4 && <motion.button onClick={handleAddPhoto} initial={{
                      opacity: 0,
                      scale: 0.8
                    }} animate={{
                      opacity: 1,
                      scale: 1
                    }} whileHover={{
                      scale: 1.02
                    }} whileTap={{
                      scale: 0.98
                    }} className="aspect-[4/3] border-2 border-dashed border-gray-300 rounded-xl hover:border-teal-deep hover:bg-teal-50/30 transition-all flex flex-col items-center justify-center gap-2 group" data-id="element-408">
                                <Camera className="w-6 h-6 text-gray-400 group-hover:text-teal-deep transition-colors" data-id="element-409" />
                                <span className="text-xs text-gray-500 group-hover:text-teal-deep transition-colors" data-id="element-410">
                                  {t.fornitore.reportProblem.step3.addPhoto}
                                </span>
                              </motion.button>}
                          </>}
                      </div>

                      <div className="bg-blue-50 border border-blue-100 rounded-xl p-4" data-id="element-411">
                        <p className="text-sm text-blue-900" data-id="element-412">
                          💡 {t.fornitore.reportProblem.step3.tip}
                        </p>
                      </div>
                    </motion.div>}

                  {currentStep === 4 && <motion.div initial={{
                opacity: 0,
                x: 20
              }} animate={{
                opacity: 1,
                x: 0
              }} className="space-y-6" data-id="element-413">
                      <div data-id="element-414">
                        <h3 className="text-xl font-bold text-navy mb-2" data-id="element-415">
                          {t.fornitore.reportProblem.step4.title}
                        </h3>
                        <p className="text-sm text-gray-500" data-id="element-416">
                          {t.fornitore.reportProblem.step4.subtitle}
                        </p>
                      </div>

                      <Card className="p-6 space-y-4 border-2 border-gray-200" data-id="element-417">
                        <div data-id="element-418">
                          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 block" data-id="element-419">
                            {t.fornitore.reportProblem.step4.type}
                          </label>
                          <div className="flex items-center gap-2" data-id="element-420">
                            <span className="text-2xl" data-id="element-421">
                              {getProblemTypeEmoji()}
                            </span>
                            <span className="px-3 py-1 bg-teal-100 text-teal-700 rounded-full text-sm font-semibold border border-teal-200" data-id="element-422">
                              {getProblemTypeLabel()}
                            </span>
                          </div>
                        </div>

                        <div data-id="element-423">
                          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 block" data-id="element-424">
                            {t.fornitore.reportProblem.step4.resolvability}
                          </label>
                          {getResolvabilityBadge() && <span className={`inline-block px-3 py-1 rounded-full text-sm font-semibold border ${getResolvabilityBadge()?.color}`} data-id="element-425">
                              {getResolvabilityBadge()?.text}
                            </span>}
                        </div>

                        <div data-id="element-426">
                          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 block" data-id="element-427">
                            {t.fornitore.reportProblem.step4.area}
                          </label>
                          <p className="text-navy font-medium" data-id="element-428">
                            {step2Data.affectedArea && t.fornitore.reportProblem.step2.affectedArea.options[step2Data.affectedArea]}
                          </p>
                        </div>

                        {selectedProblem === 'brokenItem' && step2Data.damageSeverity && <div data-id="element-429">
                              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 block" data-id="element-430">
                                {t.fornitore.reportProblem.step4.severity}
                              </label>
                              <div className="flex items-center gap-2" data-id="element-431">
                                <div className="flex gap-1" data-id="element-432">
                                  {[1, 2, 3, 4, 5].map(level => <div key={level} className={`w-4 h-4 rounded-full transition-all ${level <= parseInt(step2Data.damageSeverity!) ? severityLabels[parseInt(step2Data.damageSeverity!)]?.dotColor : 'bg-gray-200'}`} data-id="element-433" />)}
                                </div>
                                <span className={`font-semibold ${severityLabels[parseInt(step2Data.damageSeverity)]?.color}`} data-id="element-434">
                                  {getSeverityLabel()}
                                </span>
                              </div>
                            </div>}

                        <div data-id="element-435">
                          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 block" data-id="element-436">
                            {t.fornitore.reportProblem.step4.description}
                          </label>
                          <p className="text-gray-700 text-sm leading-relaxed bg-gray-50 p-3 rounded-lg" data-id="element-437">
                            {step2Data.description}
                          </p>
                        </div>

                        <div data-id="element-438">
                          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 block" data-id="element-439">
                            {t.fornitore.reportProblem.step4.photos}
                          </label>
                          {photos.length > 0 ? <div className="grid grid-cols-4 gap-2" data-id="element-440">
                              {photos.map((photo, index) => <div key={index} className="aspect-square rounded-lg overflow-hidden border border-gray-200" data-id="element-441">
                                  <img src={photo} alt={`Photo ${index + 1}`} className="w-full h-full object-cover" data-id="element-442" />
                                </div>)}
                            </div> : <p className="text-gray-400 text-sm italic" data-id="element-443">
                              {t.fornitore.reportProblem.step4.noPhotos}
                            </p>}
                        </div>
                      </Card>

                      <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-100 rounded-xl" data-id="element-444">
                        <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" data-id="element-445" />
                        <p className="text-sm text-blue-900" data-id="element-446">
                          {t.fornitore.reportProblem.step4.infoNote}
                        </p>
                      </div>
                    </motion.div>}
                </div>

                {/* Footer with CTAs */}
                <div className="flex items-center justify-between gap-3 p-6 border-t border-gray-200 flex-shrink-0" data-id="element-447">
                  <Button onClick={onClose} className="px-6 bg-gray-100 hover:bg-gray-200 text-gray-700" data-id="element-448">
                    {t.fornitore.reportProblem.cancel}
                  </Button>

                  {currentStep === 1 && <Button onClick={handleStep1Continue} disabled={!selectedProblem} className={`px-6 ${!selectedProblem ? 'opacity-40 cursor-not-allowed' : ''}`} data-id="element-449">
                      {t.fornitore.reportProblem.continue}
                    </Button>}

                  {currentStep === 2 && <Button onClick={handleStep2Continue} disabled={!isStep2Valid()} className={`px-6 ${!isStep2Valid() ? 'opacity-40 cursor-not-allowed' : ''}`} data-id="element-450">
                      {t.fornitore.reportProblem.continue}
                    </Button>}

                  {currentStep === 3 && <Button onClick={handleStep3Continue} disabled={!isStep3Valid()} className={`px-6 ${!isStep3Valid() ? 'opacity-40 cursor-not-allowed' : ''}`} data-id="element-451">
                      {t.fornitore.reportProblem.continue}
                    </Button>}

                  {currentStep === 4 && <Button onClick={handleFinalSubmit} className="px-6" data-id="element-452">
                      {t.common.submit}
                    </Button>}
                </div>
              </motion.div>
            </div>
          </div>
        </>}

      {showConfirmation && <ReportConfirmationModal isOpen={showConfirmation} onClose={handleConfirmationClose} problemType={getProblemTypeLabel()} isBlocking={step2Data?.canResolve === 'noBlocking'} estimatedTime={getEstimatedTime()} data-id="element-453" />}
    </AnimatePresence>;
}